import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  Chip,
  Grid,
  CircularProgress,
} from "@mui/material";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import StorageIcon from "../../../assets/images/storage.svg";
import HeaderInfo from "../../../assets/images/headerInfo.svg";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CustomInput from "../../component/common/inputField";
import SearchIcon from "@mui/icons-material/Search";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import customerServices from "../../../services/CustomerService";
import loginService from "../../../services/LoginServices";
import { CustomerViewType } from "../../../types/customerEnrolment";
import { showToast } from "../../component/common/toastNotification";
import { useTranslation } from "react-i18next";
import LockResetIcon from "@mui/icons-material/LockReset";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import { AxiosError } from "axios";
import {
  BRAND,
  NJIM,
  containerSx,
  glassCardSx,
  iconBubbleSx,
  pageShellSx,
  primaryBtnSx,
  secondaryBtnSx,
  sectionTitleRowSx,
  softBlockSx,
} from "../../../theme/njimTheme";
import {
  identityPasswordPolicyMessage,
  localizeEnrolmentSyncDescription,
  localizeEnrolmentSyncStatus,
} from "../../../helper/enrolmentI18n";

const extractApiErrorMessage = (error: unknown, fallback: string): string => {
  const axiosErr = error as AxiosError;
  const data = axiosErr.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  if (Array.isArray(data) && data.length > 0) return data.map(String).join(". ");
  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    if (typeof obj.status === "string" && obj.status.trim()) return obj.status.trim();
    if (typeof obj.message === "string") return obj.message;
    if (Array.isArray(obj.errors)) return obj.errors.map(String).join(". ");
  }
  return fallback;
};

const readAccountCreated = (customer: CustomerViewType = {}): boolean => {
  const raw = customer as Record<string, unknown>;
  return Boolean(customer.accountCreated ?? raw.AccountCreated);
};

const CustomerEnrolment: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncData, setSyncData] = useState<CustomerViewType>({});
  const [customerCode, setCustomerCode] = useState("");
  const [customerSyncStatus, setCustomerSyncStatus] = useState(false);
  const [customerData, setCustomerData] = useState<CustomerViewType>({});
  const [quickAccessData, setQuickAccessData] = useState<CustomerViewType[]>(
    []
  );
  const [resetPassword, setResetPassword] = useState("");
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState("");
  const [resetLoginUsername, setResetLoginUsername] = useState("");
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [showResetPw, setShowResetPw] = useState(false);
  const [showResetPwConfirm, setShowResetPwConfirm] = useState(false);

  const getCustomerEmail = (customer: CustomerViewType = customerData) =>
    (
      syncData.email ||
      customer.contactEmail ||
      customer.emailAddress ||
      customer.email ||
      ""
    ).trim();

  const getLoginIdentityCandidates = (customer: CustomerViewType = customerData) => {
    const code = (customer.customerCode || customerCode).trim();
    const email = getCustomerEmail(customer);
    const typed = resetLoginUsername.trim();
    return Array.from(new Set([typed, syncData.username, code, email].filter(Boolean).map((v) => String(v).trim())));
  };

  const isCustomerSynced =
    syncData?.status === "In System (Synced)" || readAccountCreated(customerData);

  const hasSelectedCustomer = Boolean(customerData.customerCode?.trim());

  const resetPasswordPolicyError = resetPassword
    ? identityPasswordPolicyMessage(resetPassword, t)
    : null;
  const passwordsMatch =
    Boolean(resetPassword) &&
    Boolean(resetPasswordConfirm) &&
    resetPassword === resetPasswordConfirm;
  const canSubmitPasswordReset =
    hasSelectedCustomer &&
    passwordsMatch &&
    !resetPasswordPolicyError &&
    !resetPasswordLoading;

  const hasCredentialData = Boolean(syncData?.username || syncData?.password);
  const CREDENTIALS_CACHE_KEY = "customerCredentialsCache";

  const getCredentialsCache = (): Record<string, CustomerViewType> => {
    try {
      const raw = localStorage.getItem(CREDENTIALS_CACHE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  };

  const saveCredentialForCustomer = (
    customerCodeValue: string,
    data: Partial<CustomerViewType>
  ) => {
    if (!customerCodeValue) return;
    const cache = getCredentialsCache();
    cache[customerCodeValue] = {
      ...(cache[customerCodeValue] || {}),
      ...data,
    };
    localStorage.setItem(CREDENTIALS_CACHE_KEY, JSON.stringify(cache));
  };

  const getCachedCredential = (customerCodeValue: string): CustomerViewType => {
    const cache = getCredentialsCache();
    return cache[customerCodeValue] || {};
  };

  const formatValue = (value?: string | null) => {
    return value && value.trim() !== "" ? value : "-";
  };
  const effectCalled = useRef(false);

  useEffect(() => {
    if (!effectCalled.current) {
      fetchQuickAccess();
      effectCalled.current = true;
    }
  }, []);

  const fetchQuickAccess = async () => {
    try {
      const response = await customerServices.getQuickAccessList();
      const finalResponse = response?.data;
      if (finalResponse && finalResponse.length > 0) {
        setQuickAccessData(finalResponse);
      }
    } catch (error) {
      console.error("Error fetching quick access list:", error);
    }
  };

  const handleCustomerEnrolDetails = async (id: string) => {
    try {
      setSearchLoading(true);
      setLoading(true);

      const response = await customerServices.getCustomerEnrollById(id);

      if (response.data.items && response.data.items.length > 0) {
        const customer = response.data.items[0];
        setCustomerData(customer);
        const code = customer.customerCode || "";
        const cachedCredentials = getCachedCredential(code);
        setResetLoginUsername(code);
        setSyncData((prev) => ({
          ...prev,
          username: code || prev.username,
          password: cachedCredentials.password || prev.password,
          email:
            cachedCredentials.email ||
            customer.contactEmail ||
            customer.email ||
            prev.email,
        }));

        handleSyncCustomerData(customer.id ?? "", customer);

        showToast(t("customerEnrolment.customerDetailsLoaded"), "success");
      } else {
        setCustomerData({});
        showToast(t("customerEnrolment.noValidCustomerRecord"), "error");
      }
    } catch (error) {
      console.error("Error fetching order details:", error);
    } finally {
      setSearchLoading(false);
      setLoading(false);
    }
  };

  const mergeSyncResponse = (
    responseData: CustomerViewType,
    customer: CustomerViewType
  ) => {
    const code = customer.customerCode || customerCode;
    const cached = getCachedCredential(code);
    const mergedData = {
      ...responseData,
      username: responseData?.username || code || cached.username,
      password: responseData?.password || cached.password,
      email:
        responseData?.email ||
        customer.contactEmail ||
        customer.emailAddress ||
        customer.email ||
        cached.email,
    };
    setSyncData(mergedData);
    if (mergedData.username) {
      setResetLoginUsername(String(mergedData.username));
    }
    if (mergedData.username || mergedData.password || mergedData.email) {
      saveCredentialForCustomer(code, mergedData);
    }
    return mergedData;
  };

  const handleSyncCustomerData = async (
    id: string,
    customerOverride?: CustomerViewType
  ) => {
    const customer = customerOverride ?? customerData;
    try {
      setSyncLoading(true);
      const response = await customerServices.SyncCustomer(id);

      if (response.status === 200) {
        mergeSyncResponse(response.data ?? {}, customer);
      }
    } catch (error) {
      console.error("Error fetching sync status:", error);
      showToast(
        extractApiErrorMessage(
          error,
          t("customerEnrolment.syncStatusFailed", {
            defaultValue: "Could not load portal sync status for this customer.",
          })
        ),
        "error"
      );
    } finally {
      setSyncLoading(false);
    }
  };

  const handleReSyncData = async (
    id: string,
    customerOverride?: CustomerViewType
  ) => {
    const customer = customerOverride ?? customerData;
    try {
      setSyncLoading(true);
      const response = await customerServices.ReSyncCustomer(id);

      if (response.status === 200) {
        mergeSyncResponse(response.data ?? {}, customer);
        setCustomerData((prev) => ({ ...prev, accountCreated: true }));
        showToast(t("customerEnrolment.customerDataSynced"), "success");
        setCustomerSyncStatus(true);
        await handleSyncCustomerData(id, { ...customer, accountCreated: true });
      }
    } catch (error) {
      console.error("Error syncing customer:", error);
      const apiStatus = extractApiErrorMessage(error, "");
      const hint =
        apiStatus.includes("Failed to create user account") ||
        apiStatus.includes("Failed to reSync user account")
          ? t("customerEnrolment.syncFailedPortalMayExist", {
              defaultValue:
                " If the portal account already exists, you can still set a password using Reset Password below.",
            })
          : apiStatus.includes("not Allowed to be synced")
            ? t("customerEnrolment.syncFailedSageFlag", {
                defaultValue:
                  " Enable online sync for this customer in Sage (AllowSyncwithApp = 2), or use Reset Password if they already have a portal account.",
              })
            : "";
      showToast(
        extractApiErrorMessage(
          error,
          t("customerEnrolment.syncFailed", {
            defaultValue:
              "Could not sync customer to the portal. Check Sage online-sync eligibility (AllowSyncwithApp) or contact support.",
          })
        ) + hint,
        "error"
      );
    } finally {
      setSyncLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!canSubmitPasswordReset) return;

    const loginCandidates = getLoginIdentityCandidates();
    if (loginCandidates.length === 0) {
      showToast(t("customerEnrolment.noCustomerSelected", { defaultValue: "No customer selected" }), "error");
      return;
    }
    const policyError = identityPasswordPolicyMessage(resetPassword, t);
    if (policyError) {
      showToast(policyError, "error");
      return;
    }
    if (resetPassword !== resetPasswordConfirm) {
      showToast(t("customerEnrolment.passwordsMustMatch", { defaultValue: "Passwords do not match" }), "error");
      return;
    }
    try {
      setResetPasswordLoading(true);
      const customerId = customerData.id?.trim();
      // Mark user for forced password change on next login (Customers API sets
      // MustChangePassword=true). Then apply the admin-chosen password via auth/reset.
      if (customerId) {
        await customerServices.resetCustomerPortalPassword(customerId);
      }
      await loginService.resetUserPassword(loginCandidates, resetPassword);
      const loginUsername = loginCandidates[0];
      // Portal account exists — unlock synced UI even if Re-sync failed (e.g. Sage flag).
      setCustomerData((prev) => ({ ...prev, accountCreated: true }));
      setSyncData((prev) => ({
        ...prev,
        status: "In System (Synced)",
        username: prev.username || loginUsername,
      }));
      showToast(
        t("customerEnrolment.passwordResetSuccess", { user: loginUsername }),
        "success"
      );
      setResetPassword("");
      setResetPasswordConfirm("");
    } catch (error) {
      const apiMsg = extractApiErrorMessage(error, "");
      const needsSync = /no portal account|use \/sync first/i.test(apiMsg);
      showToast(
        extractApiErrorMessage(
          error,
          t(
            needsSync
              ? "customerEnrolment.passwordResetFailedNoAccount"
              : "customerEnrolment.passwordResetFailedNeedSync"
          )
        ),
        "error"
      );
    } finally {
      setResetPasswordLoading(false);
    }
  };

  return (
    <Box sx={pageShellSx} py={3}>
      <Box sx={containerSx}>
        {/* ===== Search Section ===== */}
        <Card sx={glassCardSx}>
          <Box sx={sectionTitleRowSx}>
            <Box sx={iconBubbleSx}>
              <img
                src={StorageIcon}
                alt=""
                style={{
                  width: 18,
                  height: 18,
                  filter:
                    "invert(17%) sepia(39%) saturate(2910%) hue-rotate(197deg) brightness(90%) contrast(97%)",
                }}
              />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary }}>
                {t("customerEnrolment.title")}
              </Typography>
              <Typography variant="body2" color={NJIM.textSecondary}>
                {t("customerEnrolment.syncCustomerData")}
              </Typography>
            </Box>
          </Box>

          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              if (!customerCode || searchLoading) return;
              handleCustomerEnrolDetails(customerCode);
            }}
            sx={{
              display: "flex",
              alignItems: "flex-end",
              gap: 2,
              flexWrap: "wrap",
              mt: 2.25,
            }}
          >
          <Box sx={{ flex: 1, minWidth: 220 }}>
          <CustomInput
            type="text"
            label={t("customerEnrolment.customerCodeSage")}
            name="customerCode"
            placeholder={t("customerEnrolment.enterCustomerCode")}
            value={customerCode}
            onChange={(e) => {
              setCustomerCode(e.target.value);
              setCustomerSyncStatus(false);
              setSyncData({});
            }}
            marginBottom="0px"
          />
          </Box>
            <Box sx={{ display: "flex", gap: 1.25, alignItems: "center" }}>
            <Button
              type="submit"
              variant="contained"
              className="main_btn mx-2"
                sx={primaryBtnSx}
              disabled={!customerCode || searchLoading}
              startIcon={
                searchLoading ? undefined : <SearchIcon sx={{ fontSize: 20 }} />
              }
            >
              {searchLoading ? (
                <CircularProgress size={20} sx={{ color: NJIM.white }} />
              ) : (
                t("common.search")
              )}
            </Button>
            {customerCode && (
              <Button
                type="button"
                variant="outlined"
                className="gray_btn"
                  sx={secondaryBtnSx}
                onClick={() => {
                  setCustomerCode("");
                  setCustomerData({});
                  setCustomerSyncStatus(false);
                  setSyncData({});
                }}
              >
                {t("common.clear")}
              </Button>
            )}
          </Box>
        </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              flexWrap: "wrap",
              mt: 2,
            }}
          >
            <Typography fontSize={13} color={NJIM.textSecondary} sx={{ fontWeight: 700 }}>
              {t("customerEnrolment.quickAccess")}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
            {quickAccessData.map((item) => (
              <Chip
                key={item.id}
                label={item.customerCode}
                className="gray_btn"
                sx={{
                    fontWeight: 700,
                    borderRadius: 999,
                    px: 1.25,
                    bgcolor: "rgba(255,255,255,0.55)",
                    border: "1px solid rgba(148,163,184,0.28)",
                    backdropFilter: "blur(10px)",
                    WebkitBackdropFilter: "blur(10px)",
                  "&:hover": {
                      bgcolor: "rgba(255,255,255,0.75)",
                      borderColor: "rgba(10,61,120,0.22)",
                  },
                }}
                clickable
                onClick={() => {
                  setCustomerCode(item.customerCode ?? "");
                  setCustomerSyncStatus(false);
                  setSyncData({});
                }}
              />
            ))}
          </Stack>
        </Box>
        <Card
          sx={{
            mt: 2,
              p: { xs: 1.75, sm: 2 },
              borderRadius: 4,
              background:
                "linear-gradient(135deg, rgba(10,61,120,0.08), rgba(56,189,248,0.06))",
              border: "1px solid rgba(10,61,120,0.16)",
          }}
        >
            <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary, mb: 1 }}>
            {t("customerEnrolment.loginCredentials")}
          </Typography>
          {hasCredentialData ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.username")}
                </Typography>
                  <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary }}>
                  {syncData.username || customerData.customerCode || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.password")}
                </Typography>
                  <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary }}>
                  {syncData.password || t("customerEnrolment.passwordNotReturned")}
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.contactEmail")}
                </Typography>
                  <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary }}>
                  {syncData.email || "-"}
                </Typography>
              </Grid>
            </Grid>
          ) : (
            <Typography color={NJIM.textSecondary} fontSize={13}>
              {t("customerEnrolment.credentialsWillAppear")}
            </Typography>
          )}
        </Card>
        </Card>

        {loading ? (
          <Card sx={{ ...glassCardSx, p: 5, textAlign: "center" }}>
            <CircularProgress size={40} sx={{ color: BRAND }} />
          </Card>
        ) : !customerData.customerCode ? (
          <Card sx={glassCardSx}>
            <Box display="flex" justifyContent="center" mt={1.5}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 4,
                  display: "grid",
                  placeItems: "center",
                  background:
                    "linear-gradient(135deg, rgba(10,61,120,0.12), rgba(56,189,248,0.08))",
                  border: "1px solid rgba(10,61,120,0.16)",
                }}
              >
                <img
                  src={StorageIcon}
                  alt=""
                  style={{
                    width: 22,
                    height: 22,
                    filter:
                      "invert(17%) sepia(39%) saturate(2910%) hue-rotate(197deg) brightness(90%) contrast(97%)",
                  }}
                />
              </Box>
            </Box>
            <Typography textAlign="center" mt={2} sx={{ fontWeight: 800 }}>
              {t("customerEnrolment.noCustomerData")}
            </Typography>
            <Typography textAlign="center" mt={0.75} color={NJIM.textSecondary}>
              {t("customerEnrolment.enterCustomerCodeToRetrieve")}
            </Typography>
          </Card>
        ) : (
          <>
            {/* ===== Sync Status Section ===== */}
            {customerSyncStatus && (
              <Card
                sx={{
                  ...glassCardSx,
                  p: 2.25,
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1.25,
                  background:
                    "linear-gradient(135deg, rgba(34,197,94,0.10), rgba(255,255,255,0.62))",
                  border: "1px solid rgba(34,197,94,0.18)",
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 3,
                    display: "grid",
                    placeItems: "center",
                    background: "rgba(34,197,94,0.12)",
                    border: "1px solid rgba(34,197,94,0.18)",
                    color: NJIM.success,
                    flex: "0 0 auto",
                  }}
                >
                  <CheckCircleOutlinedIcon sx={{ fontSize: 22 }} />
                </Box>
                <Typography color={NJIM.textPrimary} sx={{ fontWeight: 700 }}>
                  {t("customerEnrolment.newCustomerCreated")} {syncData.email || "-"}.
                  {" "}
                  {t("customerEnrolment.username")} {syncData.username || customerCode}
                  {" "}
                  {t("customerEnrolment.password")}{" "}
                  {syncData.password || t("customerEnrolment.passwordNotReturned")}
                </Typography>
              </Card>
            )}
            <Card sx={glassCardSx}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                alignItems={{ xs: "stretch", md: "center" }}
              >
              <Box
                sx={{
                  width: 45,
                  height: 45,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor:
                    syncData?.status === "In System (Synced)"
                        ? "rgba(10,61,120,0.12)"
                      : syncData?.status === "Not Eligible (No Sync)"
                          ? "rgba(239,68,68,0.12)"
                          : "rgba(245,158,11,0.14)",
                  color:
                    syncData?.status === "In System (Synced)"
                        ? BRAND
                      : syncData?.status === "Not Eligible (No Sync)"
                          ? NJIM.danger
                          : NJIM.warning,
                    border: "1px solid rgba(148,163,184,0.22)",
                    boxShadow: "0 12px 28px rgba(2,6,23,0.10)",
                }}
              >
                {syncData?.status === "In System (Synced)" ? (
                  <CheckCircleOutlinedIcon sx={{ fontSize: 22 }} />
                ) : syncData?.status === "Not Eligible (No Sync)" ? (
                  <CancelOutlinedIcon sx={{ fontSize: 22 }} />
                ) : (
                  <ReportProblemOutlinedIcon sx={{ fontSize: 22 }} />
                )}
              </Box>

              <Box flexGrow={1}>
                  <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary }}>
                  {t("customerEnrolment.syncStatus")}{" "}
                  <Chip
                    label={localizeEnrolmentSyncStatus(
                      syncData?.status,
                      t,
                      t("common.notAvailable")
                    )}
                      sx={{
                        ml: 1,
                        fontWeight: 800,
                        bgcolor: "rgba(10,61,120,0.12)",
                        color: BRAND,
                        border: "1px solid rgba(10,61,120,0.18)",
                      }}
                    size="small"
                  />
                </Typography>
                <Typography color={NJIM.textSecondary} mt={1}>
                  {localizeEnrolmentSyncDescription(syncData.description, t)}
                </Typography>
                {syncData?.status === "In System (Synced)" && (
                  <Typography color={NJIM.textSecondary} mt={1}>
                    {t("customerEnrolment.lastSynced")}:
                    <br />
                    {syncData?.lastSyncDate ?? t("common.notAvailable")}
                  </Typography>
                )}
              </Box>
              {syncData?.status === "Not Eligible (No Sync)" ? (
                <Box
                    sx={{
                      color: NJIM.danger,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      fontWeight: 800,
                      whiteSpace: "nowrap",
                      px: 1.25,
                      py: 1,
                      borderRadius: 2.5,
                      background: "rgba(239,68,68,0.10)",
                      border: "1px solid rgba(239,68,68,0.18)",
                    }}
                >
                    <CancelOutlinedIcon sx={{ fontSize: 18 }} />
                  {t("customerEnrolment.syncNotAvailable")}
                </Box>
              ) : (
                <Button
                  variant="contained"
                  className="main_btn"
                    sx={{ ...primaryBtnSx, width: { xs: "100%", md: "auto" } }}
                  onClick={() => handleReSyncData(customerData.id ?? "")}
                  disabled={syncLoading}
                >
                  {syncLoading ? (
                    <>
                      <CircularProgress
                        size={20}
                          sx={{ color: NJIM.white }}
                      />
                        <Typography sx={{ px: 2, fontWeight: 800 }}>
                          {t("customerEnrolment.updating")}
                        </Typography>
                    </>
                  ) : (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <img
                          src={StorageIcon}
                          alt=""
                          style={{
                            filter: "brightness(0) invert(1)",
                            width: 16,
                            height: 16,
                          }}
                        />
                        <span>{t("customerEnrolment.updateReSyncCustomer")}</span>
                      </Stack>
                  )}
                </Button>
              )}
            </Stack>
          </Card>
          {/* ===== Reset Password Section ===== */}
          {hasSelectedCustomer && (
            <Card sx={{ ...glassCardSx, border: "1px solid rgba(245,158,11,0.22)", background: "linear-gradient(135deg, rgba(245,158,11,0.06), rgba(255,255,255,0.66))" }}>
              {!isCustomerSynced && (
                <Box
                  sx={{
                    mb: 2,
                    p: 1.5,
                    borderRadius: 2,
                    background: "rgba(245,158,11,0.10)",
                    border: "1px solid rgba(245,158,11,0.22)",
                  }}
                >
                  <Typography sx={{ fontSize: "13px", color: NJIM.warning, fontWeight: 600 }}>
                    {t("customerEnrolment.syncOptionalForReset", {
                      defaultValue:
                        "Portal sync is not complete for this customer. You can still set a password below if they already have a portal account — otherwise run Update & Re-sync first.",
                    })}
                  </Typography>
                </Box>
              )}
              <Stack direction="row" alignItems="center" spacing={1.5} mb={2.5}>
                <Box sx={{ width: 36, height: 36, borderRadius: "12px", display: "grid", placeItems: "center", background: "rgba(245,158,11,0.12)", border: "1px solid rgba(245,158,11,0.22)", color: NJIM.warning }}>
                  <LockResetIcon sx={{ fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary, fontSize: "15px" }}>
                    {t("customerEnrolment.resetPassword", { defaultValue: "Reset Customer Password" })}
                  </Typography>
                  <Typography sx={{ fontSize: "12px", color: NJIM.textSecondary }}>
                    {t("customerEnrolment.resetPasswordDesc", {
                      defaultValue:
                        "Set a temporary password (min 6 chars, upper + lower + digit + special). The customer will change it on next login.",
                    })}
                  </Typography>
                </Box>
              </Stack>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("customerEnrolment.loginUsername", { defaultValue: "Portal login username" })}
                    value={resetLoginUsername}
                    onChange={(e) => setResetLoginUsername(e.target.value)}
                    helperText={t("customerEnrolment.loginUsernameHint", {
                      defaultValue: "Usually the customer code; some older accounts use email.",
                    })}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "12px", background: "rgba(255,255,255,0.72)" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("customerEnrolment.newPassword", { defaultValue: "New Password" })}
                    type={showResetPw ? "text" : "password"}
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowResetPw((p) => !p)}>
                            {showResetPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    label={t("customerEnrolment.confirmPassword", { defaultValue: "Confirm Password" })}
                    type={showResetPwConfirm ? "text" : "password"}
                    value={resetPasswordConfirm}
                    onChange={(e) => setResetPasswordConfirm(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowResetPwConfirm((p) => !p)}>
                            {showResetPwConfirm ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />
                </Grid>
                <Grid item xs={12} md={2} sx={{ display: "flex", alignItems: "flex-end" }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleResetPassword}
                    disabled={!canSubmitPasswordReset}
                    sx={{
                      borderRadius: "10px",
                      textTransform: "none",
                      fontWeight: 700,
                      background: `linear-gradient(135deg, ${NJIM.warning}, ${NJIM.warning})`,
                      boxShadow: "0 2px 8px rgba(180,83,9,0.25)",
                      "&:hover": { boxShadow: "0 4px 16px rgba(180,83,9,0.35)" },
                      "&.Mui-disabled": {
                        background: "rgba(180,83,9,0.35)",
                        color: "rgba(255,255,255,0.7)",
                      },
                    }}
                  >
                    {resetPasswordLoading ? <CircularProgress size={18} sx={{ color: NJIM.white }} /> : t("customerEnrolment.resetPasswordBtn", { defaultValue: "Reset" })}
                  </Button>
                </Grid>
              </Grid>
              {resetPassword && resetPasswordConfirm && resetPassword !== resetPasswordConfirm && (
                <Typography sx={{ color: NJIM.danger, fontSize: "12px", mt: 1 }}>
                  {t("customerEnrolment.passwordsMustMatch", { defaultValue: "Passwords do not match" })}
                </Typography>
              )}
              {resetPassword && resetPasswordPolicyError && (
                <Typography sx={{ color: NJIM.danger, fontSize: "12px", mt: 1 }}>
                  {resetPasswordPolicyError}
                </Typography>
              )}
            </Card>
          )}

          {/* ===== Customer Header Information ===== */}
            <Card sx={glassCardSx}>
              <Box sx={sectionTitleRowSx}>
                <Box sx={iconBubbleSx}>
                  <img
                    src={HeaderInfo}
                    alt=""
                    style={{
                      width: 18,
                      height: 18,
                      filter:
                        "invert(17%) sepia(39%) saturate(2910%) hue-rotate(197deg) brightness(90%) contrast(97%)",
                    }}
                  />
                </Box>
                <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary }}>
                  {t("customerEnrolment.customerHeaderInformation")}
                </Typography>
              </Box>
            <Grid container spacing={2}>
              <Grid item md={6}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.customerCode")}
                </Typography>
                  <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                  {formatValue(customerData.customerCode)}
                </Typography>
              </Grid>

              <Grid item md={6}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.customerName")}
                </Typography>
                  <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                  {formatValue(customerData.customerName)}{" "}
                </Typography>
              </Grid>

              <Grid item md={6}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.customerCategory")}
                </Typography>
                <Chip
                  label={formatValue(customerData.customerCategory)}
                  size="small"
                  variant="outlined"
                    sx={{
                      borderRadius: 999,
                      bgcolor: "rgba(255,255,255,0.55)",
                      borderColor: "rgba(148,163,184,0.35)",
                      fontWeight: 800,
                    }}
                />
              </Grid>

              <Grid item md={6}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.paymentTerm")}
                </Typography>
                <Chip
                  label={formatValue(customerData.paymentTerm)}
                  size="small"
                  variant="outlined"
                    sx={{
                      borderRadius: 999,
                      bgcolor: "rgba(255,255,255,0.55)",
                      borderColor: "rgba(148,163,184,0.35)",
                      fontWeight: 800,
                    }}
                />
              </Grid>
            </Grid>
          </Card>
          {/* ===== Address Information ===== */}
            <Card sx={glassCardSx}>
              <Box sx={sectionTitleRowSx}>
                <Box sx={iconBubbleSx}>
                  <LocationOnOutlinedIcon sx={{ color: BRAND, fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary }}>
                  {t("customerEnrolment.addressInformation")}
                </Typography>
              </Box>

            <Grid container spacing={2}>
              <Grid item md={6}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.addressCode")}
                </Typography>
                  <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                    {formatValue(customerData.addressCode)}
                  </Typography>
              </Grid>

              <Grid item md={6}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.contactEmail")}
                </Typography>
                  <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                    {formatValue(customerData.contactEmail)}
                  </Typography>
              </Grid>
              <Grid item md={6}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.addressLine1")}
                </Typography>
                  <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                    {formatValue(customerData.addressDescription1)}
                  </Typography>
              </Grid>

              <Grid item md={6}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.addressLine2")}
                </Typography>
                  <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                    {formatValue(customerData.addressDescription2)}
                  </Typography>
              </Grid>
              <Grid item md={6}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.onlineShipmentSite")}
                </Typography>
                <Chip
                  label={formatValue(customerData.onlineShipmentSite)}
                  size="small"
                  variant="outlined"
                  sx={{
                      borderRadius: 999,
                      background:
                        "linear-gradient(135deg, rgba(10,61,120,0.95), rgba(29,78,216,0.95))",
                      color: NJIM.white,
                      borderColor: "rgba(255,255,255,0.25)",
                      fontWeight: 800,
                  }}
                />
              </Grid>

              <Grid item md={6}>
                <Typography variant="body2" color={NJIM.textSecondary}>
                  {t("customerEnrolment.onlineReceivingSite")}
                </Typography>
                <Chip
                  label={formatValue(customerData.onlineReceivingSite)}
                  size="small"
                  variant="outlined"
                  sx={{
                      borderRadius: 999,
                      background:
                        "linear-gradient(135deg, rgba(10,61,120,0.95), rgba(29,78,216,0.95))",
                      color: NJIM.white,
                      borderColor: "rgba(255,255,255,0.25)",
                      fontWeight: 800,
                  }}
                />
              </Grid>
            </Grid>
          </Card>
          {/* ===== Statistical Groups ===== */}
            <Card sx={glassCardSx}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Box sx={iconBubbleSx}>
                  <TrendingUpIcon sx={{ color: BRAND, fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary }}>
                  {t("customerEnrolment.statisticalGroups")}
                </Typography>
            </Stack>

            <Grid container spacing={3}>
              {/* Row 1 */}
              <Grid item xs={12}>
                  <Box sx={softBlockSx}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.statisticalGroup1Code")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.statGroup1Code)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.statisticalGroup1Description")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.statGroup1Description)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Row 2 */}
              <Grid item xs={12}>
                  <Box sx={softBlockSx}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.statisticalGroup2Code")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.statGroup2Code)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.statisticalGroup2Description")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.statGroup2Description)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Row 3 */}
              <Grid item xs={12}>
                  <Box sx={softBlockSx}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.statisticalGroup3Code")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.statGroup3Code)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.statisticalGroup3Description")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.statGroup3Description)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Card>
          {/* ===== Sales Hierarchy ===== */}
            <Card sx={glassCardSx}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Box sx={iconBubbleSx}>
                  <TrendingUpIcon sx={{ color: BRAND, fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary }}>
                  {t("customerEnrolment.salesHierarchy")}
                </Typography>
            </Stack>

            <Grid container spacing={2}>
              {/* Row 1 */}
              <Grid item xs={12}>
                  <Box sx={softBlockSx}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.supervisorCode")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.supervisorCode)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.supervisorName")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.supervisorName)}{" "}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Row 2 */}
              <Grid item xs={12}>
                  <Box sx={softBlockSx}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.salesManagerCode")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.salesManagerCode)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.salesManagerName")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.salesManagerName)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Row 3 */}
              <Grid item xs={12}>
                  <Box sx={softBlockSx}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.nationalManagerCode")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.nationalManagerCode)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.nationalManagerName")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.nationalManagerName)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Card>
          {/* ===== Assigned Sales Representatives ===== */}
            <Card sx={glassCardSx}>
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <Box sx={iconBubbleSx}>
                  <GroupOutlinedIcon sx={{ color: BRAND, fontSize: 20 }} />
                </Box>
                <Typography sx={{ fontWeight: 800, color: NJIM.textPrimary }}>
                {t("customerEnrolment.assignedSalesRepresentatives")}
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              {/* Row 1 */}
              <Grid item xs={12}>
                  <Box sx={softBlockSx}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.primarySaleRepCode")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.salesRep1Code)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.primarySaleRepName")}
                      </Typography>
                      <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            fontWeight: 700,
                            color: NJIM.textPrimary,
                          }}
                      >
                          <PersonOutlineIcon sx={{ width: 18 }} />
                        {formatValue(customerData.salesRep1Name)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              {/* Row 2 */}
              <Grid item xs={12}>
                  <Box sx={softBlockSx}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.secondarySaleRepCode")}
                      </Typography>
                        <Typography sx={{ fontWeight: 700, color: NJIM.textPrimary }}>
                        {formatValue(customerData.salesRep2Code)}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" color={NJIM.textSecondary}>
                        {t("customerEnrolment.secondarySaleRepName")}
                      </Typography>
                      <Typography
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.75,
                            fontWeight: 700,
                            color: NJIM.textPrimary,
                          }}
                      >
                          <PersonOutlineIcon sx={{ width: 18 }} />
                        {formatValue(customerData.salesRep2Name)}
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </>
      )}
      </Box>
    </Box>
  );
};

export default CustomerEnrolment;
